import { auth } from "@lumberset/api-client";
import { Button, Field, Heading, Hint, Input, toast } from "@lumberset/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";

export const Route = createFileRoute("/_auth/reset-password")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ResetPassword,
});

function ResetPassword() {
  const { token } = Route.useSearch();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!token) {
    return (
      <div className="flex flex-col gap-y-4">
        <Heading>This link has expired</Heading>
        <p className="txt-small text-ui-fg-subtle">
          Reset links work once, for one hour. Ask for a new one.
        </p>
        <Button asChild variant="secondary" size="large" block>
          <Link to="/forgot-password">Send a new link</Link>
        </Button>
      </div>
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    const result = await auth.resetPassword({
      newPassword: String(new FormData(event.currentTarget).get("password")),
      token,
    });
    setBusy(false);
    if (result.error) {
      setError(result.error.message ?? "That link no longer works. Ask for a new one.");
      return;
    }
    toast.success("Password changed. Sign in with the new one.");
    await navigate({ to: "/login", search: { redirect: undefined } });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-y-5">
      <Heading>Choose a new password</Heading>
      <Field label="New password" hint="At least 10 characters.">
        {(control) => (
          <Input
            {...control}
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={10}
            required
            size="large"
            autoFocus
          />
        )}
      </Field>
      {error ? <Hint variant="error">{error}</Hint> : null}
      <Button type="submit" size="large" block isLoading={busy}>
        Save password
      </Button>
    </form>
  );
}
